import { lockScroll, unlockScroll } from '@theme/utilities';

class SmidProjectGallery extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.track = this.querySelector('[data-sps-track]');
    if (!this.track) return;
    this.collectArticleMedia();
    this.slides = [...this.querySelectorAll('[data-sps-slide]')];
    this.hidden = this.slides.length === 0;
    if (!this.slides.length) return;
    this.slides.forEach((slide, index) => {
      slide.inert = false;
      slide.setAttribute('aria-label', `${index + 1} van ${this.slides.length}`);
    });
    this.track.querySelectorAll('video').forEach(video => {
      const fit = () => this.setVideoRatio(video);
      fit();
      video.addEventListener('loadedmetadata', fit, options);
    });
    this.prepareCover();
    this.dialog = this.querySelector('[data-sps-dialog]');
    this.stage = this.querySelector('[data-sps-stage]');
    this.index = 0;
    if (typeof this.dialog?.showModal !== 'function') return;
    this.querySelectorAll('[data-sps-enlarge]').forEach(link => {
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-controls', this.dialog.id);
    });
    this.addEventListener('click', event => {
      const link = event.target.closest('[data-sps-enlarge]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.trigger = link;
      this.index = this.slides.indexOf(link.closest('[data-sps-slide]'));
      this.renderDialog();
      this.dialog.showModal();
      lockScroll(this.dialog);
      this.pauseVideos();
    }, options);
    this.querySelector('[data-sps-close]').addEventListener('click', () => this.dialog.close(), options);
    this.querySelector('[data-sps-dialog-prev]').addEventListener('click', () => this.goTo(this.index - 1), options);
    this.querySelector('[data-sps-dialog-next]').addEventListener('click', () => this.goTo(this.index + 1), options);
    this.dialog.addEventListener('keydown', event => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.target.closest('video, input, textarea, select')) return;
      const target = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 }[event.key];
      if (target === undefined) return;
      event.preventDefault();
      this.goTo(target);
    }, options);
    this.dialog.addEventListener('click', event => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
    }, options);
    this.dialog.addEventListener('close', () => {
      this.stage.querySelectorAll('video').forEach(video => video.pause());
      this.stage.replaceChildren();
      unlockScroll(this.dialog);
      this.trigger?.focus({ preventScroll: true });
    }, options);
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.pauseVideos(); }, options);
    if ('IntersectionObserver' in window) {
      this.visibility = new IntersectionObserver(entries => {
        entries.forEach(entry => { if (!entry.isIntersecting) entry.target.pause(); });
      }, { threshold: 0.05 });
      this.track.querySelectorAll('video').forEach(video => this.visibility.observe(video));
    }
    document.addEventListener('shopify:block:select', event => {
      const slide = this.slides.find(item => item === event.target || item.contains(event.target));
      slide?.scrollIntoView({ block: 'center', behavior: 'instant' });
    }, options);
  }

  setVideoRatio(video) {
    if (!video.videoWidth || !video.videoHeight) return;
    const slide = video.closest('[data-sps-slide]');
    if (!slide) return;
    const ratio = video.videoWidth / video.videoHeight;
    slide.style.setProperty('--sps-video-ratio', ratio);
    slide.dataset.videoOrientation = ratio >= 1 ? 'landscape' : 'portrait';
  }

  prepareCover() {
    const project = this.closest('.project-detail, .smid-client-project');
    const intro = project?.querySelector('.sp-project-split__intro, .smid-client-project__header');
    const firstImage = this.slides.map(slide => slide.querySelector('img')).find(Boolean);
    if (!intro || !firstImage || intro.querySelector('.sp-project-cover')) return;
    const cover = document.createElement('div');
    cover.className = 'sp-project-cover';
    cover.setAttribute('aria-hidden', 'true');
    const image = firstImage.cloneNode(true);
    image.removeAttribute('id');
    image.alt = '';
    image.loading = 'eager';
    image.fetchPriority = 'high';
    image.sizes = '100vw';
    cover.append(image);
    intro.prepend(cover);
  }

  mediaBase(src) {
    if (!src) return '';
    try {
      const url = new URL(src, document.baseURI);
      if (!['http:', 'https:'].includes(url.protocol)) return '';
      url.search = '';
      url.hash = '';
      return url.href;
    } catch { return ''; }
  }

  collectArticleMedia() {
    const source = this.dataset.sourceId && document.getElementById(this.dataset.sourceId);
    if (!source || this.contains(source)) return;
    const featured = this.querySelector('[data-sps-featured] img');
    const featuredBase = featured ? this.mediaBase(featured.getAttribute('src')) : '';
    const candidates = [...source.querySelectorAll('img, video')].filter(media => !this.contains(media) && !(media.tagName === 'IMG' && media.closest('video')));
    for (const media of candidates) {
      if (!source.contains(media)) continue;
      const video = media.tagName === 'VIDEO';
      const mediaSrc = media.getAttribute('src') || media.querySelector('source')?.getAttribute('src');
      if (!video && !mediaSrc) continue;
      const anchor = media.closest('a');
      if (anchor && source.contains(anchor)) {
        // Preserve links to products, articles, downloads or mixed linked content inline.
        const linkBase = this.mediaBase(anchor.getAttribute('href'));
        const isMediaLink = linkBase && (/\.(?:avif|gif|heic|jpe?g|png|webp|svg)$/i.test(new URL(linkBase).pathname) || linkBase === this.mediaBase(mediaSrc));
        const pureMedia = !anchor.textContent.trim() && [...anchor.querySelectorAll('*')].every(child => ['IMG', 'PICTURE', 'SOURCE', 'SPAN'].includes(child.tagName)) && anchor.querySelectorAll('img').length === 1;
        if (video || !isMediaLink || !pureMedia || anchor.hasAttribute('download')) continue;
      }
      const picture = !video && media.closest('picture');
      const moving = anchor && source.contains(anchor) ? anchor : picture || media;
      const originalParent = moving.parentElement;
      const figure = media.closest('figure');
      const caption = figure?.querySelector('figcaption')?.textContent.trim() || media.getAttribute('alt') || media.getAttribute('aria-label') || media.getAttribute('title') || '';
      const duplicatedFeatured = !video && featuredBase && this.mediaBase(mediaSrc) === featuredBase && (!anchor || this.mediaBase(anchor.getAttribute('href')) === featuredBase);
      if (duplicatedFeatured) {
        moving.remove();
        this.removeEmptyMediaWrappers(originalParent, source);
        continue;
      }
      const slide = document.createElement('div');
      slide.className = 'sps-slide';
      slide.dataset.spsSlide = '';
      slide.dataset.caption = caption;
      slide.dataset.mediaType = video ? 'video' : 'photo';
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'projectbeeld');
      if (video) {
        media.pause();
        media.autoplay = false;
        media.removeAttribute('autoplay');
        media.controls = true;
        media.playsInline = true;
        media.preload = 'metadata';
        media.classList.add('sps-video');
        slide.append(media);
      } else {
        const link = moving.tagName === 'A' ? moving : document.createElement('a');
        if (link !== moving) {
          link.href = media.src;
          link.target = '_blank';
          link.rel = 'noopener';
          link.append(moving);
        }
        link.classList.add('sps-photo');
        link.dataset.spsEnlarge = '';
        link.setAttribute('aria-label', `${caption || 'Projectfoto'} (foto vergroten)`);
        media.loading = this.track.querySelector('[data-sps-slide]') ? 'lazy' : 'eager';
        media.decoding = 'async';
        media.sizes = '(max-width: 700px) calc(100vw - 40px), (max-width: 1000px) 50vw, 840px';
        slide.append(link);
      }
      this.track.append(slide);
      this.removeEmptyMediaWrappers(originalParent, source);
    }
  }

  removeEmptyMediaWrappers(element, source) {
    const wrappers = new Set(['P', 'DIV', 'SPAN', 'A', 'FIGURE', 'PICTURE', 'STRONG', 'EM', 'B', 'I']);
    while (element && element !== source && wrappers.has(element.tagName) && !element.id && !element.getAttribute('aria-label')) {
      const empty = [...element.childNodes].every(node => node.nodeType === Node.COMMENT_NODE || (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) || (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR'));
      if (!empty) break;
      const parent = element.parentElement;
      element.remove();
      element = parent;
    }
  }


  pauseVideos() { this.querySelectorAll('video').forEach(video => video.pause()); }

  goTo(index) {
    this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(index)));
    this.renderDialog();
  }

  renderDialog() {
    this.stage.querySelectorAll('video').forEach(video => video.pause());
    const slide = this.slides[this.index];
    const source = slide.querySelector('video');
    this.dialog.dataset.mediaType = source ? 'video' : 'photo';
    if (source) {
      const ratio = source.videoWidth && source.videoHeight ? source.videoWidth / source.videoHeight : Number(slide.style.getPropertyValue('--sps-video-ratio')) || 9 / 16;
      this.dialog.style.setProperty('--sps-video-ratio', ratio);
    }
    let media;
    if (source) {
      media = source.cloneNode(true);
      media.removeAttribute('id');
      media.autoplay = false;
      media.controls = true;
      media.preload = 'metadata';
    } else {
      const image = slide.querySelector('img');
      media = document.createElement('img');
      media.src = slide.querySelector('[data-sps-enlarge]').href;
      media.alt = image?.alt || slide.dataset.caption;
      media.width = Number(image?.getAttribute('width')) || 1200;
      media.height = Number(image?.getAttribute('height')) || 1600;
    }
    this.stage.replaceChildren(media);
    this.querySelector('[data-sps-dialog-caption]').textContent = slide.dataset.caption;
    this.querySelector('[data-sps-dialog-count]').textContent = `${this.index + 1} / ${this.slides.length}`;
    this.querySelector('[data-sps-dialog-prev]').disabled = this.index === 0;
    this.querySelector('[data-sps-dialog-next]').disabled = this.index === this.slides.length - 1;
  }


  disconnectedCallback() {
    this.pauseVideos();
    if (this.dialog?.open) this.dialog.close();
    if (this.dialog) unlockScroll(this.dialog);
    this.visibility?.disconnect();
    this.controller?.abort();
    this.controller = null;
  }
}

if (!customElements.get('smid-project-slider')) customElements.define('smid-project-slider', SmidProjectGallery);
